import type { ApplicationResourceApi } from '@ingenico/api-sdk-device-management-public';
import type {
  AdvancedSearchApi,
  AdvancedSearchField,
  AutocompleteQuickFilterOption,
  DatagridQuickFilter,
  DatagridQuickFilterTypeEnum,
} from '@ingenico/react-ui/advanced-search';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import type { GridColDef, GridEventListener, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import type { GridCallbackDetails, GridSortModel, GridApiPremium } from '@mui/x-data-grid-premium';
import type { MutableRefObject, RefObject } from 'react';
import type { TFunction } from 'react-i18next';
import type { ApplicationResourceFront } from '../../domain/models/applicationList.model';
import type { SelectedRowsPerPage } from '../../hooks/useManagePaginatedDatagridRowSelection.hook';

export interface UseApplicationsListColumnsHook {
  readonly columns: GridColDef[];
  readonly defaultAvailableColumns: string[];
}

export interface ApplicationListGridProps {
  readonly handleRowClick: GridEventListener<'rowClick'>;
  readonly ref: MutableRefObject<GridApiPremium>;
  readonly getRowId: (e: ApplicationResourceApi) => string;
  readonly total: number;
  readonly handleSortModelChange: (sort: GridSortModel, details: GridCallbackDetails) => void;
  readonly handlePaginationModelChange: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>,
  ) => void;
  readonly sortModel: GridSortModel;
  readonly pageSize: number;
  readonly page: number;
}

export interface ApplicationListVMArgs {
  readonly selectedRowIds?: string[];
}

export interface ApplicationListVMOutput {
  readonly search: SearchData;
  readonly quickFiltersSearch: QuickFilterSearchData;
  readonly columns: GridColDef[];
  readonly gridProps: ApplicationListGridProps;
  readonly noRowsProps: EmptyListProps;
  readonly t: TFunction;
  readonly isFirstLoading: boolean;
  readonly shouldDisplayGrid: boolean;
  readonly response: ApplicationListData;
  readonly accumulatedData: ApplicationResourceFront[];
  readonly selectionModel: GridRowSelectionModel;
  readonly setSelectionModel: (selectionModel: GridRowSelectionModel) => SelectedRowsPerPage;
  readonly selectedRowsPerPage: SelectedRowsPerPage;
}

export interface SearchData {
  readonly handleSearchQueryChange: (v: string) => void;
  readonly searchQuery: string;
}

export interface QuickFilterSearchData {
  readonly filterRefApi: RefObject<AdvancedSearchApi>;
  readonly isLoadingQuickFilters: boolean;
  readonly fields: AdvancedSearchField[];
  readonly getLabelQuickFilterLabel: (name: string) => string;
  readonly overrideDefaultFilterTypes: (
    field: string,
    currentType: DatagridQuickFilterTypeEnum,
  ) => DatagridQuickFilterTypeEnum;
  readonly quickFilterCall: () => Promise<AutocompleteQuickFilterOption[]>;
  readonly checkAllowReset: (filter?: string) => boolean;
  readonly handleQuickFilterChange: (quickFilters: DatagridQuickFilter[]) => void;
  // readonly currentQuickFilters: DatagridQuickFilter[];
}

export interface ApplicationListData {
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly data: ApplicationResourceFront[];
  readonly error?: unknown;
  readonly isSuccess?: boolean;
  readonly rowCount: number;
}

export enum ApplicationStatusEnum {
  PUBLISHED = 'PUBLISHED',
  DEPRECATED = 'DEPRECATED',
}
